// PlanDayComments — docs/FIX-PLANS.md §3/§4. Church-scoped thread on one
// reading_plan_day: avatar, name, time, text, like count, Reply (one level),
// Report. Visible only to members of the plan's OWN church — a follower
// from another church who comments does so against THEIR OWN church_id for
// this same plan_day_id (a separate thread), per §4's rule.
//
// Self-contained report flow (inserts into `reports` directly) rather than
// threading a callback through this component's two different mount points
// (ReadingPlanDaySheet is opened both from BibleApp.jsx's openPlan state and
// from FullSermonSheet.jsx's dayOpen state) — BibleApp.jsx's REPORT_TABLE
// map already routes content_type:'plan_day_comment' to this table, so the
// existing moderation UI picks these up with no further wiring.
//
// Props:
//   planDayId, churchId, userId
//   placeholder — the day's reflection question, used as the input placeholder

import { useCallback, useEffect, useState } from "react";
import { supabase } from "../supabase";
import FaceStack from "./FaceStack";

const GOLD = "var(--gold)";
const SANS = "'Plus Jakarta Sans', system-ui, sans-serif";
const REPORT_REASONS = ["Spam", "Inappropriate", "Harassment", "Other"];

function fmtRelative(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diffMs = Date.now() - d.getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return "now";
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d`;
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
}

export default function PlanDayComments({ planDayId, churchId, userId, placeholder }) {
  const [comments, setComments] = useState(null); // null = loading
  const [profilesById, setProfilesById] = useState({});
  const [likesByComment, setLikesByComment] = useState({}); // id -> { count, likedByMe }
  const [draft, setDraft] = useState("");
  const [replyTo, setReplyTo] = useState(null); // comment id | null
  const [replyDraft, setReplyDraft] = useState("");
  const [posting, setPosting] = useState(false);
  const [expandedReplies, setExpandedReplies] = useState(() => new Set());
  const [reportingId, setReportingId] = useState(null);

  const load = useCallback(async () => {
    if (!planDayId || !churchId) { setComments([]); return; }
    const { data, error } = await supabase
      .from("reading_plan_day_comments")
      .select("*")
      .eq("plan_day_id", planDayId)
      .eq("church_id", churchId)
      .eq("removed", false)
      .order("created_at", { ascending: true });
    if (error) { console.warn("load plan day comments:", error.message); setComments([]); return; }
    const rows = data || [];
    setComments(rows);

    const userIds = [...new Set(rows.map(r => r.user_id))];
    if (userIds.length) {
      const { data: profs } = await supabase.from("profiles").select("id, full_name, avatar_url").in("id", userIds);
      const m = {};
      for (const p of (profs || [])) m[p.id] = p;
      setProfilesById(m);
    }

    if (rows.length) {
      const { data: likes } = await supabase.from("reading_plan_day_comment_likes")
        .select("comment_id, user_id").in("comment_id", rows.map(r => r.id));
      const byComment = {};
      for (const l of (likes || [])) {
        if (!byComment[l.comment_id]) byComment[l.comment_id] = { count: 0, likedByMe: false };
        byComment[l.comment_id].count++;
        if (l.user_id === userId) byComment[l.comment_id].likedByMe = true;
      }
      setLikesByComment(byComment);
    }
  }, [planDayId, churchId, userId]);

  useEffect(() => { load(); }, [load]);

  async function postComment(text, parentId) {
    const body = (text || "").trim();
    if (!body || !userId || !churchId || posting) return;
    setPosting(true);
    const { error } = await supabase.from("reading_plan_day_comments").insert({
      plan_day_id: planDayId, church_id: churchId, user_id: userId, parent_id: parentId || null, text: body,
    });
    setPosting(false);
    if (error) { console.warn("post plan day comment:", error.message); return; }
    if (parentId) { setReplyDraft(""); setReplyTo(null); setExpandedReplies(s => new Set(s).add(parentId)); }
    else setDraft("");
    load();
  }

  async function toggleLike(commentId) {
    if (!userId) return;
    const liked = likesByComment[commentId]?.likedByMe;
    setLikesByComment(prev => {
      const cur = prev[commentId] || { count: 0, likedByMe: false };
      return { ...prev, [commentId]: { count: cur.count + (liked ? -1 : 1), likedByMe: !liked } };
    });
    if (liked) {
      await supabase.from("reading_plan_day_comment_likes").delete().eq("comment_id", commentId).eq("user_id", userId);
    } else {
      await supabase.from("reading_plan_day_comment_likes").insert({ comment_id: commentId, user_id: userId, church_id: churchId });
    }
  }

  async function submitReport(commentId, reason) {
    if (!userId) return;
    await supabase.from("reports").insert({
      reporter_id: userId, content_type: "plan_day_comment", content_id: commentId, reason, church_id: churchId,
    });
    setReportingId(null);
  }

  if (comments === null) return <div style={S.loading}>Loading comments…</div>;

  const topLevel = comments.filter(c => !c.parent_id);
  const repliesByParent = {};
  for (const c of comments) if (c.parent_id) (repliesByParent[c.parent_id] = repliesByParent[c.parent_id] || []).push(c);

  function Row({ c, isReply }) {
    const prof = profilesById[c.user_id] || {};
    const likeInfo = likesByComment[c.id] || { count: 0, likedByMe: false };
    const replies = repliesByParent[c.id] || [];
    const showReplies = expandedReplies.has(c.id);
    return (
      <div style={{ display: "flex", gap: 10, padding: "12px 0", borderTop: "1px solid var(--border)" }}>
        <FaceStack userIds={[c.user_id]} profilesById={{ [c.user_id]: prof }} max={1} size={32}/>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 12.5 }}><b>{prof.full_name || "Someone"}</b> <span style={{ color: "var(--stone)" }}>· {fmtRelative(c.created_at)}</span></div>
          <div style={{ fontSize: 14, lineHeight: 1.45, color: "var(--ink)", marginTop: 2 }}>{c.text}</div>
          <div style={{ display: "flex", gap: 14, alignItems: "center", marginTop: 6 }}>
            <button onClick={() => toggleLike(c.id)} style={{ ...S.actionLink, color: likeInfo.likedByMe ? "var(--gold-ink)" : "var(--stone)" }}>
              ♡ {likeInfo.count || ""}
            </button>
            {!isReply && <button onClick={() => setReplyTo(replyTo === c.id ? null : c.id)} style={S.actionLink}>Reply</button>}
            {!isReply && replies.length > 0 && (
              <button onClick={() => setExpandedReplies(s => { const n = new Set(s); n.has(c.id) ? n.delete(c.id) : n.add(c.id); return n; })} style={{ ...S.actionLink, color: "var(--gold-ink)" }}>
                {showReplies ? "Hide replies" : `${replies.length} ${replies.length === 1 ? "reply" : "replies"}`}
              </button>
            )}
            <button onClick={() => setReportingId(reportingId === c.id ? null : c.id)} style={{ ...S.actionLink, marginLeft: "auto" }}>Report</button>
          </div>

          {reportingId === c.id && (
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 8 }}>
              {REPORT_REASONS.map(r => (
                <button key={r} onClick={() => submitReport(c.id, r)} style={S.reportChip}>{r}</button>
              ))}
            </div>
          )}

          {!isReply && replyTo === c.id && (
            <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
              <input autoFocus value={replyDraft} onChange={e => setReplyDraft(e.target.value)} placeholder="Write a reply…"
                style={S.input} onKeyDown={e => { if (e.key === "Enter") postComment(replyDraft, c.id); }}/>
              <button onClick={() => postComment(replyDraft, c.id)} disabled={posting || !replyDraft.trim()} style={S.sendBtn}>Send</button>
            </div>
          )}

          {!isReply && showReplies && replies.map(r => <Row key={r.id} c={r} isReply/>)}
        </div>
      </div>
    );
  }

  return (
    <div style={S.wrap}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, background: "var(--cream)", borderRadius: 999, padding: "10px 14px", marginBottom: 4 }}>
        <input value={draft} onChange={e => setDraft(e.target.value)} placeholder={placeholder || "Add a comment…"}
          style={{ flex: 1, border: "none", background: "none", outline: "none", fontSize: 13.5, color: "var(--ink)", fontFamily: SANS }}
          onKeyDown={e => { if (e.key === "Enter") postComment(draft, null); }}/>
        <button onClick={() => postComment(draft, null)} disabled={posting || !draft.trim()} style={{ ...S.sendBtn, background: draft.trim() ? GOLD : "var(--border)" }}>Post</button>
      </div>

      {topLevel.length === 0 ? (
        <div style={S.loading}>No comments yet — be the first to share.</div>
      ) : topLevel.map(c => <Row key={c.id} c={c}/>)}
    </div>
  );
}

const S = {
  wrap: { display: "flex", flexDirection: "column" },
  loading: { fontSize: 13, color: "var(--stone)", padding: "16px 4px" },
  actionLink: { background: "none", border: "none", color: "var(--stone)", fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: SANS, padding: 0 },
  reportChip: { background: "var(--border)", border: "none", borderRadius: 999, padding: "5px 11px", fontSize: 11.5, fontWeight: 700, color: "var(--ink)", cursor: "pointer", fontFamily: SANS },
  input: { flex: 1, border: "1px solid var(--border-strong)", borderRadius: 999, padding: "9px 14px", fontSize: 13, color: "var(--ink)", background: "var(--white)", outline: "none", fontFamily: SANS },
  sendBtn: { flexShrink: 0, border: "none", borderRadius: 999, padding: "8px 14px", fontSize: 12.5, fontWeight: 800, color: "var(--ink)", cursor: "pointer", fontFamily: SANS },
};
